import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, HardHat, Headset, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { FadeIn, MaskedLineInView, SectionHeading } from "@/components/Reveal";
import { ABOUT_HEADER_IMAGE, FABRICATION_IMAGE, FACADE_IMAGE, SERVICES } from "@/data/site";

const PILLARS = [
    {
        icon: BadgeCheck,
        title: "Quality & Reliability",
        text: "Every system we supply is engineered for dependable performance under real site conditions — with careful attention to structure, mechanics and safe operation.",
    },
    {
        icon: Headset,
        title: "Professional Service",
        text: "Straightforward advice, clear communication and dependable support — from the first enquiry through installation and beyond.",
    },
    {
        icon: HardHat,
        title: "Safety-Focused Design",
        text: "Safety is engineered into every system we deliver — because lifting equipment is only as good as the way it protects the people who use it.",
    },
];

const SAFETY_POINTS = [
    "Safety-first approach to every system we design",
    "Engineering focused on stability and secure operation",
    "Support from enquiry through installation and after",
    "Solutions matched to your site, loads and workflow",
];

export default function About() {
    return (
        <main data-testid="about-page">
            <PageHeader
                eyebrow="About Us"
                title={
                    <>
                        Engineering you
                        <br />
                        can build on
                    </>
                }
                lead="UMA ENGINEERING provides professional engineering and lifting-system solutions for construction sites, high-rise buildings and industrial facilities."
                image={ABOUT_HEADER_IMAGE}
            />

            {/* INTRO SPLIT */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-12">
                    <div>
                        <SectionHeading
                            eyebrow="Who we are"
                            title={
                                <>
                                    Professional engineering,
                                    <br />
                                    <span className="text-accent">reliable systems</span>
                                </>
                            }
                        />
                        <FadeIn delay={0.15}>
                            <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
                                From mono rail systems and tower hoists to BMUs, double decker
                                hoists, floor cranes and suspended rope platforms, we engineer
                                lifting systems that move materials and people safely, reliably
                                and efficiently.
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.2}>
                            <ul className="mt-8 space-y-4">
                                {SAFETY_POINTS.map((point) => (
                                    <li
                                        key={point}
                                        className="flex items-start gap-3 text-sm text-slate-300"
                                    >
                                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-accent/50">
                                            <Check className="h-3 w-3 text-accent" />
                                        </span>
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </FadeIn>
                        <FadeIn delay={0.25}>
                            <Link
                                to="/services"
                                data-testid="about-services-link"
                                className="group mt-10 inline-flex items-center gap-3 bg-accent px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-night transition-colors duration-300 hover:bg-accent-hover"
                            >
                                Explore Our Services
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </Link>
                        </FadeIn>
                    </div>
                    <FadeIn delay={0.1} className="relative">
                        <img
                            src={FABRICATION_IMAGE}
                            alt="Engineers reviewing technical drawings in a steel fabrication workshop"
                            loading="lazy"
                            className="aspect-[4/3] w-full border border-line object-cover"
                        />
                        <div
                            className="absolute -bottom-4 -left-4 hidden h-full w-full border border-accent/40 sm:block"
                            aria-hidden="true"
                        />
                    </FadeIn>
                </div>
            </section>

            {/* PILLARS */}
            <section className="border-t border-line bg-deck py-20 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-12">
                    <SectionHeading
                        eyebrow="How we work"
                        title={
                            <>
                                The principles behind
                                <br />
                                every <span className="text-accent">system</span>
                            </>
                        }
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-3">
                        {PILLARS.map((p, i) => (
                            <FadeIn key={p.title} delay={i * 0.1}>
                                <div
                                    data-testid={`about-pillar-${i}`}
                                    className="group h-full border border-line bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-accent/50"
                                >
                                    <span className="flex h-12 w-12 items-center justify-center border border-accent/40 bg-night/60 text-accent">
                                        <p.icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                        {p.text}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* SAFETY BAND */}
            <section className="relative overflow-hidden border-t border-line">
                <img
                    src={FACADE_IMAGE}
                    alt="Facade maintenance work at height"
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-night/80" />
                <div className="grain inset-0" />
                <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
                    <MaskedLineInView>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent">
                            Safety
                        </p>
                    </MaskedLineInView>
                    <MaskedLineInView delay={0.1}>
                        <blockquote className="mt-5 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">
                            Safety is not an add-on.{" "}
                            <span className="text-stroke">It is the way we design.</span>
                        </blockquote>
                    </MaskedLineInView>
                </div>
            </section>

            {/* WHAT WE DELIVER */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
                    <FadeIn>
                        <h2 className="font-display text-4xl font-bold uppercase leading-[0.95] text-white sm:text-5xl">
                            What we deliver
                        </h2>
                        <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
                            A focused range of lifting and access systems — each engineered,
                            supplied and supported by the same team.
                        </p>
                        <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {SERVICES.map((s) => (
                                <li
                                    key={s.id}
                                    data-testid={`about-service-item-${s.id}`}
                                    className="flex items-center gap-3 border border-line bg-card px-4 py-3.5 font-display text-lg font-semibold uppercase text-slate-200"
                                >
                                    <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-accent" />
                                    {s.name}
                                </li>
                            ))}
                        </ul>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <div className="relative">
                            <img
                                src={FACADE_IMAGE}
                                alt="High-rise facade access work"
                                loading="lazy"
                                className="aspect-[4/5] w-full border border-line object-cover"
                            />
                            <div
                                className="absolute -right-4 -top-4 hidden h-full w-full border border-line lg:block"
                                aria-hidden="true"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
}
